import { useForm } from "../../../../hooks/useForm/useForm";
import { Database } from "../../../../../../lib/supabase";
import { FormProps } from "../../../form-types";
import {
	BankAccountEntityType,
	SupabaseTableEnum,
} from "../../../../../../lib/supabase/supabaseTypes";
import { CustomTextField } from "../../../form-input-components/custom-input-components/custom-text-field.component";
import { CurrencySelect } from "../../../form-input-components/selects/currency-select";
import { CountrySelectShadcn } from "../../../form-input-components/selects/country-select/country.select";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { Currency } from "@/dentlab/src/types/enums";
import { getLabCountry } from "@/dentlab/src/lib/utils/country-utils";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { Button } from "@/dentlab/src/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/dentlab/src/components/ui/card";

// TODO: add explanations
// Name der Bank. Freilassen wenn Sie ein PC Konto benutzen.

export const BankAccountForm: React.FC<
	// To make this component usable both for create and update, we enable passing the bankAccountId (if the account exists)
	FormProps & {
		bankAccountId?: string;
	}
> = ({
	onSave = (bankAccount: BankAccountEntityType) => { },
	submitRef = null,
	bankAccountId = undefined,
}) => {
		const supabaseTable = SupabaseTableEnum.BANK_ACCOUNTS;

		const { organizationId, labCountry, getBankAccounts } = useCentralStore((state) => ({
			organizationId: state.organization?.id,
			labCountry: getLabCountry(state.organization),
			getBankAccounts: state.getBankAccounts,
		}));

		const isUpdate = bankAccountId ? true : false;
		const useFormCustomFields = {
			organization_id: organizationId,
			...(!isUpdate && { cdtr_country: labCountry }),
		};
		const useFormFetchData = isUpdate ? true : false;
		const useFormFetchId = isUpdate ? bankAccountId : undefined;

		const {
			handleInputChange,
			handleCreate,
			formData,
			formErrors,
			setFormData,
			handleValueChange,
			handleUpdate,
			resetFormData,
		} = useForm<Database["public"]["Tables"]["bank_accounts"]["Insert"]>(
			useFormCustomFields,
			supabaseTable,
			useFormFetchData,
			useFormFetchId,
			"Bankkonto",
		);

		const handleUpdateSubmit = async (
			event: React.FormEvent<HTMLFormElement>
		) => {
			return await handleUpdate(event, bankAccountId);
		};

		const handleInsertSubmit = async (
			event: React.FormEvent<HTMLFormElement>
		) => {
			return await handleCreate(event);
		};

		const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
			const { success, data, error } = isUpdate
				? await handleUpdateSubmit(event)
				: await handleInsertSubmit(event);
			if (data) {
				const bankAccount = data[0];
				onSave(bankAccount);
				getBankAccounts();
			}
			if (error) {
				showNotification({
					message: "Bankkonto konnte nicht gespeichert werden",
					type: "error",
				});
			}
		};

		const validateReferenceNumber = (value: string) => {
			if (value.length <= 13) {
				return true;
			} else {
				showNotification({
					message: "Referenznummer darf nicht länger als 13 Zeichen sein",
					type: "error",
				});

				return false;
			}
		};

		return (
			<form onSubmit={onSubmit} className="flex flex-col gap-4 max-w-lg">

				<Card>
					<CardHeader>
						<CardTitle>Informationen Bankkonto</CardTitle>
					</CardHeader>
					<CardContent className="flex flex-col gap-1">
						<CustomTextField
							label="Bankname"
							value={formData.bank_name || ""}
							name="bank_name"
							onChange={handleInputChange}
							validationError={formErrors.bank_name}
						/>
						<CustomTextField
							label="Bankort"
							value={formData.bank_city || ""}
							name="bank_city"
							onChange={handleInputChange}
							validationError={formErrors.bank_city}
						/>
					</CardContent>
				</Card>
				{/* 					<FormControlLabel
						control={
							<Checkbox
								checked={formData.is_pc_account ? true : false}
								name="is_pc_account"
								onChange={(e) => {
									setFormData({
										...formData,
										is_pc_account: e.target.checked,
										reference_number: "0000000000000",
									});
								}}
								color="primary"
							/>
						}
						label="PC-Konto"
						sx={{
							mt: "6.5px",
							width: "30%",
						}}
					/>
					<CustomTextField
						label="PC-Konto"
						value={formData.pc_account || ""}
						name="pc_account"
						onChange={handleInputChange}
						disabled={!formData.is_pc_account}
						validationError={formErrors.pc_account}
						customHelperText="PC-Konto der Bank (wird Ihnen von der Bank mitgeteilt) oder Ihr PC Konto, wenn Sie via Post abrechnen in der Form 01-20000-0."
						fullWidth
					/> */}



				<Card>
					<CardHeader>
						<CardTitle>Informationen Zahlungsempfänger (Zahlschein)</CardTitle>
					</CardHeader>
					<CardContent className="flex flex-col gap-1">
						<CustomTextField
							label="Kontoname"
							value={formData.account_name || ""}
							name="account_name"
							onChange={handleInputChange}
							validationError={formErrors.account_name}
							customHelperText="Der Name des Kontos"
							required
						/>
						<CurrencySelect
							value={formData.currency || Currency.CHF}
							onChange={(value: any) =>
								handleValueChange("currency", value)
							}
						/>
						{/* TODO: somehow make it clear in the db that this field is now for both IBAN and QR-IBAN */}
						<CustomTextField
							label="Konto (IBAN)"
							value={formData.qr_iban || ""}
							name="qr_iban"
							onChange={handleInputChange}
							validationError={formErrors.qr_iban}
							required
						/>
						<CustomTextField
							required
							label="Name"
							value={formData.cdtr_name || ""}
							name="cdtr_name"
							onChange={handleInputChange}
							validationError={formErrors.cdtr_name}
							helperText="Name bzw. Firma des Zahlungsempfängers gemäss Kontobezeichnung"
						/>
						{/* We are using only "S" for now, so the following is not needed: */}
						{/* <PaymentSlipAddressTypeSelect
					value="S"
					onChange={(value) => handleValueChange("cdtr_adrtp", value)}
					disabled={true}
				/> */}
						<CustomTextField
							label="Strasse" // If we were using "K" (ZE_ADRESS_TYP/ctdr_adrtp), we would need to use "Strasse oder Adresszeile 1"
							value={formData.cdtr_street_line_1 || ""}
							name="cdtr_street_line_1"
							onChange={handleInputChange}
							required
							validationError={formErrors.cdtr_street_line_1}
						/>
						<CustomTextField
							label="Hausnummer" // If we were using "K" (ZE_ADRESS_TYP/ctdr_adrtp), we would need to use "Hausnummer oder Adresszeile 2"
							value={formData.cdtr_house_number_line_2 || ""}
							name="cdtr_house_number_line_2"
							onChange={handleInputChange}
							required
							validationError={formErrors.cdtr_house_number_line_2}
						/>
						<CustomTextField
							label="Postleitzahl"
							value={formData.cdtr_area_code || ""}
							name="cdtr_area_code"
							onChange={handleInputChange}
							validationError={formErrors.cdtr_area_code}
							required
						/>
						<CustomTextField
							label="Ort"
							value={formData.cdtr_city || ""}
							name="cdtr_city"
							onChange={handleInputChange}
							validationError={formErrors.cdtr_city}
							required
						/>
						<div data-testid="bank-account-cdtr-country">
							<CountrySelectShadcn
								value={formData.cdtr_country?.toLowerCase() ?? ""}
								onChange={(value) =>
									handleValueChange("cdtr_country", value)
								}
								validationError={formErrors.cdtr_country}
								required
							/>
						</div>
						<CustomTextField
							label="Referenznummer"
							value={formData.reference_number || ""}
							name="reference_number"
							onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
								validateReferenceNumber(event.target.value) &&
								handleInputChange(event)
							}
							validationError={formErrors.reference_number}
							customHelperText="Wenn Sie von Ihrer Bank eine Referenznummer erhalten, geben Sie 0000000000000 (13 Nullen) ein."
							required
						/>
						{/* <CustomTextField
							label="Briefkopf"
							value={formData.letterhead || ""}
							name="letterhead"
							onChange={handleInputChange}
							validationError={formErrors.letterhead}
							customHelperText="Auf Rechnungen, KVs etc. kann dieser Text im Briefkopf verwendet werden."
						/> */}
					</CardContent>
				</Card>
				<Button type="submit">Speichern</Button>
			</form>
		);
	};
